import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, FileText, Edit, Trash2, Eye, Smartphone, Image, Video, MapPin, Link, MessageSquare, LayoutGrid, Upload, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Image Uploader Component
const ImageUploader = ({ value, onChange, token }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/gif', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez GIF, JPEG, JPG ou PNG.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Taille max: 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Construct full URL
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        onChange(fullUrl);
        toast.success('Image uploadée avec succès!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const clearImage = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      {/* Current image preview */}
      {value && (
        <div className="relative inline-block">
          <img 
            src={value} 
            alt="Preview" 
            className="h-24 w-auto rounded-lg border border-slate-200 object-cover"
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Upload zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-4 text-center transition-colors ${
          dragActive 
            ? 'border-indigo-500 bg-indigo-50' 
            : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".gif,.jpeg,.jpg,.png,image/gif,image/jpeg,image/png"
          onChange={handleInputChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
            <p className="text-sm text-slate-600">Upload en cours...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <Upload className="h-8 w-8 text-slate-400" />
            <p className="text-sm text-slate-600">
              <span className="font-medium text-indigo-600">Cliquez pour uploader</span> ou glissez-déposez
            </p>
            <p className="text-xs text-slate-400">GIF, JPEG, JPG, PNG (max 10MB)</p>
          </div>
        )}
      </div>

      {/* URL input as alternative */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">ou</span>
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Collez une URL d'image..."
          className="rounded-xl border-slate-200 h-10 text-sm flex-1"
        />
      </div>
    </div>
  );
};

// Mobile Preview Component
const MobilePreview = ({ template }) => {
  const content = template?.content || {};
  const messageType = template?.message_type || 'text';

  return (
    <div className="flex flex-col items-center">
      {/* Phone Frame */}
      <div className="relative w-[280px] h-[560px] bg-slate-900 rounded-[3rem] p-2 shadow-2xl">
        {/* Screen */}
        <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden flex flex-col">
          {/* Status Bar */}
          <div className="h-8 bg-slate-100 flex items-center justify-between px-6">
            <span className="text-xs text-slate-600">9:41</span>
            <div className="flex gap-1">
              <div className="w-4 h-2 bg-slate-400 rounded-sm"></div>
              <div className="w-4 h-2 bg-slate-400 rounded-sm"></div>
            </div>
          </div>

          {/* Header */}
          <div className="bg-indigo-700 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-indigo-700" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{content.sender_name || 'Votre Marque'}</p>
              <p className="text-indigo-200 text-xs">Message RCS vérifié</p>
            </div>
          </div>

          {/* Message Area */}
          <div className="flex-1 bg-slate-50 p-4 overflow-y-auto">
            <div className="space-y-3">
              {/* Message Bubble */}
              <div className="max-w-[90%]">
                {/* Rich Card */}
                {(messageType === 'rich_card' || messageType === 'media') && (
                  <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200">
                    {/* Media */}
                    {content.media_url ? (
                      content.media_type === 'video' ? (
                        <div className="relative h-40 bg-slate-200 flex items-center justify-center">
                          <Video className="h-12 w-12 text-slate-400" />
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                            0:30
                          </div>
                        </div>
                      ) : (
                        <div className="h-40 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                          <Image className="h-12 w-12 text-indigo-300" />
                        </div>
                      )
                    ) : content.image_url ? (
                      <img src={content.image_url} alt="Media" className="w-full h-40 object-cover" />
                    ) : null}

                    {/* Card Content */}
                    <div className="p-3">
                      {content.title && (
                        <h4 className="font-semibold text-slate-900 text-sm">{content.title}</h4>
                      )}
                      {content.description && (
                        <p className="text-slate-600 text-xs mt-1 line-clamp-2">{content.description}</p>
                      )}
                      {content.text && !content.title && (
                        <p className="text-slate-800 text-sm">{content.text}</p>
                      )}
                    </div>

                    {/* Buttons */}
                    {content.buttons && content.buttons.length > 0 && (
                      <div className="border-t border-slate-100">
                        {content.buttons.map((btn, idx) => (
                          <button
                            key={idx}
                            className="w-full py-2.5 text-indigo-600 text-sm font-medium hover:bg-indigo-50 transition-colors border-b border-slate-100 last:border-b-0 flex items-center justify-center gap-2"
                          >
                            {btn.type === 'url' && <Link className="h-4 w-4" />}
                            {btn.type === 'call' && <span>📞</span>}
                            {btn.type === 'location' && <MapPin className="h-4 w-4" />}
                            {btn.text || btn.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Carousel */}
                {messageType === 'carousel' && content.carousel_items && (
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
                    {content.carousel_items.map((item, idx) => (
                      <div key={idx} className="flex-shrink-0 w-48 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="h-24 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                          <Image className="h-8 w-8 text-indigo-300" />
                        </div>
                        <div className="p-2">
                          <h5 className="font-medium text-slate-900 text-xs">{item.title || `Slide ${idx + 1}`}</h5>
                          <p className="text-slate-500 text-xs mt-0.5 line-clamp-2">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Simple Text */}
                {messageType === 'text' && (
                  <div className="bg-white rounded-2xl shadow-sm p-3 border border-slate-200">
                    <p className="text-slate-800 text-sm whitespace-pre-wrap">{content.text || 'Votre message ici...'}</p>
                  </div>
                )}

                {/* Location */}
                {content.location && (
                  <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200 mt-2">
                    <div className="h-24 bg-emerald-100 flex items-center justify-center">
                      <MapPin className="h-8 w-8 text-emerald-600" />
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-slate-600">{content.location.address || 'Voir sur la carte'}</p>
                    </div>
                  </div>
                )}

                {/* Suggested Replies */}
                {content.suggested_replies && content.suggested_replies.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {content.suggested_replies.map((reply, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-600 rounded-full text-xs font-medium"
                      >
                        {reply}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Timestamp */}
              <p className="text-xs text-slate-400 ml-2">12:34</p>
            </div>
          </div>

          {/* Input Area */}
          <div className="bg-white border-t border-slate-200 p-3 flex items-center gap-2">
            <div className="flex-1 bg-slate-100 rounded-full px-4 py-2">
              <span className="text-sm text-slate-400">Message...</span>
            </div>
            <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Templates = ({ embedded = false }) => {
  const { token, isAdmin, isSuperAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    message_type: 'text',
    content: { 
      text: '',
      title: '',
      description: '',
      sender_name: '',
      media_url: '',
      media_type: 'image',
      buttons: [],
      suggested_replies: [],
      carousel_items: []
    },
    description: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/templates`, newTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template créé avec succès!');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du template');
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    
    try {
      await axios.put(`${API_URL}/templates/${selectedTemplate.id}`, newTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template mis à jour avec succès!');
      setIsEditDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) return;
    
    try {
      await axios.delete(`${API_URL}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template supprimé');
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setNewTemplate({
      name: template.name,
      message_type: template.message_type,
      content: template.content || { text: '' },
      description: template.description || ''
    });
    setIsEditDialogOpen(true);
  };

  const openPreviewDialog = (template) => {
    setSelectedTemplate(template);
    setIsPreviewDialogOpen(true);
  };

  const resetForm = () => {
    setNewTemplate({
      name: '',
      message_type: 'text',
      content: { 
        text: '',
        title: '',
        description: '',
        sender_name: '',
        media_url: '',
        media_type: 'image',
        buttons: [],
        suggested_replies: [],
        carousel_items: []
      },
      description: ''
    });
    setSelectedTemplate(null);
  };

  const addButton = () => {
    const buttons = newTemplate.content.buttons || [];
    if (buttons.length < 4) {
      setNewTemplate({
        ...newTemplate,
        content: {
          ...newTemplate.content,
          buttons: [...buttons, { text: '', type: 'url', url: '' }]
        }
      });
    }
  };

  const updateButton = (index, field, value) => {
    const buttons = [...(newTemplate.content.buttons || [])];
    buttons[index] = { ...buttons[index], [field]: value };
    setNewTemplate({
      ...newTemplate,
      content: { ...newTemplate.content, buttons }
    });
  };

  const removeButton = (index) => {
    const buttons = (newTemplate.content.buttons || []).filter((_, i) => i !== index);
    setNewTemplate({
      ...newTemplate,
      content: { ...newTemplate.content, buttons }
    });
  };

  const addSuggestedReply = () => {
    const replies = newTemplate.content.suggested_replies || [];
    if (replies.length < 11) {
      setNewTemplate({
        ...newTemplate,
        content: {
          ...newTemplate.content,
          suggested_replies: [...replies, '']
        }
      });
    }
  };

  const updateSuggestedReply = (index, value) => {
    const replies = [...(newTemplate.content.suggested_replies || [])];
    replies[index] = value;
    setNewTemplate({
      ...newTemplate,
      content: { ...newTemplate.content, suggested_replies: replies }
    });
  };

  const removeSuggestedReply = (index) => {
    const replies = (newTemplate.content.suggested_replies || []).filter((_, i) => i !== index);
    setNewTemplate({
      ...newTemplate,
      content: { ...newTemplate.content, suggested_replies: replies }
    });
  };

  const TemplateForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Nom du template</label>
            <Input
              value={newTemplate.name}
              onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
              required
              data-testid="template-name-input"
              className="rounded-xl border-slate-200 h-12"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Nom de l'émetteur</label>
            <Input
              value={newTemplate.content.sender_name || ''}
              onChange={(e) => setNewTemplate({ 
                ...newTemplate, 
                content: { ...newTemplate.content, sender_name: e.target.value }
              })}
              placeholder="Votre Marque"
              data-testid="sender-name-input"
              className="rounded-xl border-slate-200 h-12"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Type de message</label>
            <Select
              value={newTemplate.message_type}
              onValueChange={(value) => setNewTemplate({ ...newTemplate, message_type: value })}
            >
              <SelectTrigger className="rounded-xl h-12" data-testid="template-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Texte simple</SelectItem>
                <SelectItem value="media">Média (Image/Vidéo)</SelectItem>
                <SelectItem value="rich_card">Carte enrichie</SelectItem>
                <SelectItem value="carousel">Carrousel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditional fields based on message type */}
          {(newTemplate.message_type === 'rich_card' || newTemplate.message_type === 'media') && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Titre</label>
                <Input
                  value={newTemplate.content.title || ''}
                  onChange={(e) => setNewTemplate({ 
                    ...newTemplate, 
                    content: { ...newTemplate.content, title: e.target.value }
                  })}
                  className="rounded-xl border-slate-200 h-12"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Image</label>
                <ImageUploader 
                  value={newTemplate.content.media_url || ''}
                  onChange={(url) => setNewTemplate({ 
                    ...newTemplate, 
                    content: { ...newTemplate.content, media_url: url }
                  })}
                  token={token}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Type de média</label>
                <Select
                  value={newTemplate.content.media_type || 'image'}
                  onValueChange={(value) => setNewTemplate({ 
                    ...newTemplate, 
                    content: { ...newTemplate.content, media_type: value }
                  })}
                >
                  <SelectTrigger className="rounded-xl h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Vidéo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">
              {newTemplate.message_type === 'rich_card' ? 'Description' : 'Contenu du message'}
            </label>
            <Textarea
              value={newTemplate.content.text || newTemplate.content.description || ''}
              onChange={(e) => setNewTemplate({ 
                ...newTemplate, 
                content: { 
                  ...newTemplate.content, 
                  text: e.target.value,
                  description: e.target.value 
                }
              })}
              rows={4}
              required
              data-testid="template-content-textarea"
              className="rounded-xl border-slate-200 resize-none"
            />
          </div>

          {/* Buttons Section */}
          {(newTemplate.message_type === 'rich_card' || newTemplate.message_type === 'media') && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Boutons d'action (max 4)</label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addButton}
                  disabled={(newTemplate.content.buttons || []).length >= 4}
                  className="rounded-full"
                >
                  <Plus className="h-4 w-4 mr-1" /> Ajouter
                </Button>
              </div>
              {(newTemplate.content.buttons || []).map((btn, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    value={btn.text}
                    onChange={(e) => updateButton(idx, 'text', e.target.value)}
                    placeholder="Texte du bouton"
                    className="rounded-xl h-10 flex-1"
                  />
                  <Select
                    value={btn.type}
                    onValueChange={(value) => updateButton(idx, 'type', value)}
                  >
                    <SelectTrigger className="rounded-xl h-10 w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="url">Lien</SelectItem>
                      <SelectItem value="call">Appel</SelectItem>
                      <SelectItem value="location">Carte</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeButton(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Suggested Replies */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Réponses suggérées (max 11)</label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={addSuggestedReply}
                disabled={(newTemplate.content.suggested_replies || []).length >= 11}
                className="rounded-full"
              >
                <Plus className="h-4 w-4 mr-1" /> Ajouter
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(newTemplate.content.suggested_replies || []).map((reply, idx) => (
                <div key={idx} className="flex items-center gap-1 bg-slate-100 rounded-full pl-3 pr-1 py-1">
                  <Input
                    value={reply}
                    onChange={(e) => updateSuggestedReply(idx, e.target.value)}
                    placeholder="Réponse"
                    className="border-0 bg-transparent h-6 w-24 p-0 text-sm focus:ring-0"
                  />
                  <button 
                    type="button"
                    onClick={() => removeSuggestedReply(idx)}
                    className="text-slate-400 hover:text-red-500 p-1"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Description (interne)</label>
            <Input
              value={newTemplate.description}
              onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
              data-testid="template-description-input"
              className="rounded-xl border-slate-200 h-12"
            />
          </div>
        </div>

        {/* Live Preview */}
        <div className="flex flex-col items-center">
          <p className="text-sm font-medium text-slate-600 mb-4">Aperçu en temps réel</p>
          <MobilePreview template={newTemplate} />
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
            resetForm();
          }} 
          className="rounded-full"
        >
          Annuler
        </Button>
        <Button type="submit" className="rounded-full bg-indigo-700" data-testid="submit-template-button">
          {submitLabel}
        </Button>
      </div>
    </form>
  );

  if (loading) {
    const loadingContent = (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
      </div>
    );
    return embedded ? loadingContent : <Layout>{loadingContent}</Layout>;
  }

  const content = (
    <div className="space-y-8" data-testid="templates-page">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Templates de messages
            </h1>
            <p className="mt-2 text-slate-600">Créez et gérez vos templates RCS réutilisables</p>
          </div>
          
          {(isAdmin || isSuperAdmin) && (
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full px-6 h-12 bg-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" data-testid="create-template-button">
                  <Plus className="mr-2 h-5 w-5" />
                  Nouveau template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="create-template-dialog">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Créer un template RCS</DialogTitle>
                  <DialogDescription>Créez un template de message RCS avec prévisualisation mobile</DialogDescription>
                </DialogHeader>
                <TemplateForm onSubmit={handleCreateTemplate} submitLabel="Créer le template" />
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}

      {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="edit-template-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Modifier le template</DialogTitle>
              <DialogDescription>Modifiez votre template RCS avec prévisualisation en temps réel</DialogDescription>
            </DialogHeader>
            <TemplateForm onSubmit={handleUpdateTemplate} submitLabel="Enregistrer les modifications" />
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
          <DialogContent className="max-w-md" data-testid="preview-template-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Smartphone className="inline mr-2 h-5 w-5" />
                Aperçu mobile
              </DialogTitle>
              <DialogDescription>{selectedTemplate?.name}</DialogDescription>
            </DialogHeader>
            <MobilePreview template={selectedTemplate} />
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.length === 0 ? (
            <Card className="rounded-2xl border-slate-100 col-span-full">
              <CardContent className="p-12 text-center">
                <FileText className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-4 text-slate-500">Aucun template pour le moment</p>
                <p className="text-sm text-slate-400 mt-1">Créez votre premier template RCS</p>
              </CardContent>
            </Card>
          ) : (
            templates.map((template) => (
              <Card key={template.id} className="rounded-2xl border-slate-100 hover:border-indigo-100 transition-colors group" data-testid={`template-card-${template.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{template.name}</CardTitle>
                      <CardDescription>{template.description || 'Aucune description'}</CardDescription>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPreviewDialog(template)}
                        className="h-8 w-8 p-0"
                        data-testid={`preview-template-${template.id}`}
                      >
                        <Eye className="h-4 w-4 text-slate-500" />
                      </Button>
                      {(isAdmin || isSuperAdmin) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(template)}
                            className="h-8 w-8 p-0"
                            data-testid={`edit-template-${template.id}`}
                          >
                            <Edit className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="h-8 w-8 p-0 hover:text-red-500"
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        template.message_type === 'text' ? 'bg-slate-100 text-slate-700' :
                        template.message_type === 'media' ? 'bg-blue-100 text-blue-700' :
                        template.message_type === 'rich_card' ? 'bg-purple-100 text-purple-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {template.message_type === 'text' && <MessageSquare className="inline h-3 w-3 mr-1" />}
                        {template.message_type === 'media' && <Image className="inline h-3 w-3 mr-1" />}
                        {template.message_type === 'rich_card' && <FileText className="inline h-3 w-3 mr-1" />}
                        {template.message_type === 'carousel' && <LayoutGrid className="inline h-3 w-3 mr-1" />}
                        {template.message_type === 'text' ? 'Texte' :
                         template.message_type === 'media' ? 'Média' :
                         template.message_type === 'rich_card' ? 'Carte enrichie' : 'Carrousel'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-1">Aperçu</p>
                      <p className="text-sm text-slate-700 line-clamp-2">
                        {template.content?.text || template.content?.description || JSON.stringify(template.content).substring(0, 50)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default Templates;
